import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

function updateListCache<T>(queryClient: QueryClient, queryKey: QueryKey, update: (items: T[] | undefined) => T[]) {
    const filters = { queryKey, exact: true };
    const interruptedFetch = queryClient.isFetching(filters) > 0;

    // A response started before the realtime event must not overwrite it or revert earlier events.
    if (interruptedFetch) {
        void queryClient.cancelQueries(filters, { revert: false });
    }

    queryClient.setQueryData<T[]>(queryKey, update);

    // An interrupted initial load still needs the complete list, not just items received through events.
    if (interruptedFetch) {
        void queryClient.invalidateQueries(filters);
    }
}

type CreateItemOptions<T> = {
    queryKey: QueryKey;
    idField: keyof T;
    createFn: () => T;
    sortFn?: (items: T[]) => T[];
};

// Use upsert to prevent duplicates when eventReason "Nieuw" is received for an existing item (e.g. when the item is received first via query invalidation in a mutations onSuccess handler and afterwards via websockets).
export function useUpsertCacheListItem() {
    const queryClient = useQueryClient();

    return useCallback(
        function createOrUpdate<T>({ queryKey, idField, createFn, sortFn }: CreateItemOptions<T>) {
            const item = createFn();
            updateListCache<T>(queryClient, queryKey, (items) => {
                const current = items ?? [];
                const exists = current.some((cur) => cur[idField] === item[idField]);
                const updated = exists ? current.map((cur) => (cur[idField] === item[idField] ? item : cur)) : [...current, item];
                return sortFn ? sortFn(updated) : updated;
            });
        },
        [queryClient]
    );
}

type UpdateItemOptions<T, K extends keyof T> = {
    queryKey: QueryKey;
    idField: K;
    idToUpdate: T[K];
    updateFn: (item: T) => T;
    sortFn?: (items: T[]) => T[];
};

export function useUpdateCacheListItem() {
    const queryClient = useQueryClient();
    return useCallback(
        function update<T, K extends keyof T>({ queryKey, idField, idToUpdate, updateFn, sortFn }: UpdateItemOptions<T, K>) {
            updateListCache<T>(queryClient, queryKey, (items) => {
                if (!items) return [];
                const updated = items.map((item) => (item[idField] === idToUpdate ? updateFn(item) : item));
                return sortFn ? sortFn(updated) : updated;
            });
        },
        [queryClient]
    );
}

type RemoveItemOptions<T, IdField extends keyof T> = {
    queryKey: QueryKey;
    idField: IdField;
    idToDelete: T[IdField];
    sortFn?: (items: T[]) => T[];
};

export function useRemoveCacheListItem() {
    const queryClient = useQueryClient();

    return useCallback(
        function remove<T, IdField extends keyof T>({ queryKey, idField, idToDelete, sortFn }: RemoveItemOptions<T, IdField>) {
            updateListCache<T>(queryClient, queryKey, (items) => {
                const updated = items?.filter((item) => item[idField] !== idToDelete) ?? [];
                return sortFn ? sortFn(updated) : updated;
            });
        },
        [queryClient]
    );
}
